/**
 * Vercel Blob Storage Service for Encrypted Files
 * Replaces IPFS functionality with Vercel Blob Storage
 * Handles encrypted text data storage and retrieval
 */

import airtableService from './airtable';

/**
 * Logger utility for consistent, structured logging
 */
const logger = {
  info: (component, message, data = {}) => {
    console.log(`[${component}] ℹ️  ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  },
  success: (component, message, data = {}) => {
    console.log(`[${component}] ✅ ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  },
  error: (component, message, error = {}) => {
    console.error(`[${component}] ❌ ${message}`, {
      error: error.message || error,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });
  },
  warn: (component, message, data = {}) => {
    console.warn(`[${component}] ⚠️  ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  },
  progress: (component, message, data = {}) => {
    console.log(`[${component}] 🔄 ${message}`, {
      ...data,
      timestamp: new Date().toISOString()
    });
  }
};

class BlobStorageService {
  constructor() {
    // API endpoint for upload operations
    this.apiEndpoint = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    logger.info('BlobStorageService', 'Initialized for encrypted files', { apiEndpoint: this.apiEndpoint });
  }

  /**
   * Generate SHA-256 hash of encrypted data
   * This provides content-based addressing
   */
  async generateDataHash(encryptedData) {
    const startTime = Date.now();
    logger.progress('HASH', 'Generating SHA-256 hash of encrypted data');

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(encryptedData);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const duration = Date.now() - startTime;
      logger.success('HASH', 'SHA-256 hash generated', {
        hash: hashHex,
        duration: `${duration}ms`,
        dataSize: encryptedData.length
      });

      return hashHex;
    } catch (error) {
      logger.error('HASH', 'Failed to generate hash', error);
      throw error;
    }
  }

  /**
   * Upload encrypted data to Vercel Blob Storage
   * @param {string} encryptedData - The encrypted text data
   * @param {string} originalFileName - Original file name before encryption
   * @param {string} fileFormat - File format (e.g., 'png', 'jpg', 'pdf')
   * @returns {Promise<Object>} - Contains blob URL, hash, and metadata
   */
  async uploadEncryptedData(encryptedData, originalFileName, fileFormat) {
    const startTime = Date.now();
    logger.info('UPLOAD-ENCRYPTED', 'Starting encrypted data upload', {
      originalFileName,
      fileFormat,
      encryptedDataSize: encryptedData.length,
      readableSize: this.formatSize(encryptedData.length)
    });

    try {
      // Generate hash for the encrypted data
      const dataHash = await this.generateDataHash(encryptedData);

      logger.info('UPLOAD-ENCRYPTED', 'Preparing upload to API', {
        endpoint: `${this.apiEndpoint}/api/upload-encrypted`
      });

      // Upload to Vercel Blob via API route
      const response = await fetch(`${this.apiEndpoint}/api/upload-encrypted`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          encryptedData,
          originalFileName,
          fileFormat,
          dataHash
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      const duration = Date.now() - startTime;

      logger.success('UPLOAD-ENCRYPTED', 'Upload completed successfully', {
        blobUrl: result.url,
        hash: dataHash,
        size: encryptedData.length,
        duration: `${duration}ms`
      });

      // Save metadata to Airtable if service is configured
      let airtableResult = null;
      if (airtableService) {
        airtableResult = await airtableService.createContentFile({
          fileName: originalFileName,
          fileHash: dataHash,
          blobUrl: result.url,
          fileType: this.convertFormatToType(fileFormat),
          mimeType: `encrypted/${fileFormat}`,
          fileSize: encryptedData.length,
          accessLevel: 'Authenticated',
          status: 'Active',
          projectName: 'Encrypted Image Upload',
          isEncrypted: true
        });

        if (airtableResult.success) {
          logger.success('UPLOAD-ENCRYPTED', 'Metadata saved to Airtable', {
            recordId: airtableResult.data.id
          });
        } else if (!airtableResult.skipped) {
          logger.warn('UPLOAD-ENCRYPTED', 'Failed to save metadata to Airtable', {
            error: airtableResult.error
          });
        }
      }

      return {
        success: true,
        hash: dataHash,
        url: result.url,
        blobUrl: result.url,
        originalFileName,
        fileFormat,
        uploadDuration: `${duration}ms`,
        airtableRecordId: airtableService ? (airtableResult?.success ? airtableResult.data.id : null) : null
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('UPLOAD-ENCRYPTED', 'Upload failed', {
        ...error,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message || 'Upload failed'
      };
    }
  }

  /**
   * Retrieve encrypted data from Vercel Blob Storage
   * @param {string} blobUrl - The URL of the stored encrypted data
   * @returns {Promise<Object>} - Contains encrypted data or error
   */
  async retrieveEncryptedData(blobUrl) {
    const startTime = Date.now();
    logger.progress('RETRIEVE', 'Retrieving encrypted data from blob storage', {
      url: blobUrl
    });

    try {
      const response = await fetch(blobUrl);

      if (!response.ok) {
        throw new Error(`Failed to retrieve data: ${response.statusText}`);
      }

      const encryptedData = await response.text();
      const duration = Date.now() - startTime;

      logger.success('RETRIEVE', 'Data retrieved successfully', {
        dataSize: encryptedData.length,
        duration: `${duration}ms`
      });

      return {
        success: true,
        data: encryptedData
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('RETRIEVE', 'Retrieval failed', {
        ...error,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message || 'Failed to retrieve encrypted data'
      };
    }
  }

  /**
   * Format size to human-readable format
   */
  formatSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Convert file format extension to file type category
   * Matches the format expected by 01-1d1-content-server
   * @param {string} format - File format (PNG, JPG, MP4, etc.)
   * @returns {string} - File type category (Image, Video, etc.)
   */
  convertFormatToType(format) {
    if (!format) return 'Other';

    const formatUpper = format.toUpperCase();

    // Image formats
    if (['PNG', 'JPG', 'JPEG', 'GIF', 'BMP', 'WEBP', 'SVG', 'ICO'].includes(formatUpper)) {
      return 'Image';
    }

    // Video formats
    if (['MP4', 'AVI', 'MOV', 'WMV', 'FLV', 'MKV', 'WEBM'].includes(formatUpper)) {
      return 'Video';
    }

    // Audio formats
    if (['MP3', 'WAV', 'OGG', 'FLAC', 'AAC', 'WMA', 'M4A'].includes(formatUpper)) {
      return 'Audio';
    }

    // Document formats
    if (['PDF'].includes(formatUpper)) {
      return 'PDF';
    }

    if (['TXT', 'MD', 'DOC', 'DOCX', 'RTF', 'ODT'].includes(formatUpper)) {
      return 'Document';
    }

    // Code/Data formats
    if (['JSON', 'XML', 'YAML', 'YML'].includes(formatUpper)) {
      return 'JSON';
    }

    if (['HTML', 'CSS', 'JS', 'TS', 'JSX', 'TSX', 'PY', 'JAVA', 'C', 'CPP'].includes(formatUpper)) {
      return 'Code';
    }

    // Other formats
    if (['ZIP', 'RAR', '7Z', 'TAR', 'GZ'].includes(formatUpper)) {
      return 'Archive';
    }

    return 'Other';
  }

  /**
   * Generate QR code URL for a blob URL
   * @param {string} blobUrl - The blob URL to encode
   * @returns {string} - URL for QR code image
   */
  generateQRCode(blobUrl) {
    if (!blobUrl) return null;
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(blobUrl)}`;
  }
}

// Export singleton instance
const blobStorageService = new BlobStorageService();
export default blobStorageService;

// Named exports for convenience
export { BlobStorageService };
